import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { isAfter, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useContest } from '../../contexts/ContestContext';
import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import Button from '../buttons/Button';
import EmailForm from '../forms/Email';
import GamesList from '../lists/Games';
import GamesGroupsList from '../lists/GamesGroups';
import Timer from '../partials/Timer';
import UserBanner from '../partials/UserBanner';

import styles from '../../../styles/pages/play-groups.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    onTimerComplete: PropTypes.func,
    canPlay: PropTypes.bool,
};

const defaultProps = {
    onTimerComplete: null,
    canPlay: false,
};

function PlayGroupsPage({ page, onTimerComplete, canPlay }) {
    const intl = useIntl();
    const route = useUrlGenerator();

    const user = useUser();
    const { stats = {} } = user || {};
    const { score = null } = stats || {};

    // const { group: groupParam = null } = useParams();

    const { ended = false } = useContest() || {};
    const { name: themeName = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        games,
        participations,
        maxParticipations,
        canParticipateAt = null,
        title = null,
        description = null,
        blocks = null,
        groups = null,
        group: selectedGroup = null,
        emailFormTitle = null,
        emailFormDescription = null,
        theme: pageTheme = null,
    } = page || {};

    const { showUserScore = false } = pageTheme || {};

    const { withEmailForm = false } = selectedGroup || {};

    const [emailComplete, setEmailComplete] = useState(false);
    const onEmailComplete = useMemo(() => {
        setEmailComplete(true);
    }, [setEmailComplete]);

    const gamesCount = (games || []).length;
    const finalBlocks = useMemo(
        () =>
            (blocks || []).filter(
                ({ placement = null }) =>
                    (selectedGroup !== null && placement === 'with_group') ||
                    (selectedGroup === null && placement === 'without_group') ||
                    placement === null,
            ),
        [blocks, selectedGroup],
    );

    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    // Always false; timer is handled in Game page
    const showTimer = useMemo(
        () => canParticipateAtDate !== null && isAfter(canParticipateAtDate, now) && false,
        [canParticipateAtDate, now],
    );

    const canParticipate =
        (participations === null ||
            maxParticipations === null ||
            maxParticipations === -1 ||
            participations.length < maxParticipations) &&
        canPlay;

    const hasSplash =
        finalBlocks !== null && finalBlocks.length > 0 && finalBlocks[0].type === 'splash';

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hasSplash]: hasSplash,
                },
                themeClassName,
            ])}
        >
            {user !== null && showUserScore ? (
                <UserBanner
                    user={user}
                    points={score || null}
                    prefix={
                        <FormattedMessage
                            defaultMessage="Total score:"
                            description="Banner label"
                        />
                    }
                />
            ) : null}

            {gamesCount > 0 && !ended ? (
                <>
                    {finalBlocks !== null && finalBlocks.length > 0 ? (
                        <Blocks className={styles.blocks} isContainer blocks={finalBlocks} />
                    ) : (
                        <div className={styles.content}>
                            {description !== null ? <p>{description}</p> : null}
                            {title !== null ? <h1 className={styles.title}>{title}</h1> : null}
                        </div>
                    )}
                    {showTimer ? (
                        <div className={styles.timerContainer}>
                            <p>
                                <FormattedMessage
                                    defaultMessage="You will be able to play in:"
                                    description="Timer description"
                                />
                            </p>
                            <Timer
                                className={styles.timer}
                                date={canParticipateAtDate}
                                started
                                onEnded={onTimerComplete}
                            />
                        </div>
                    ) : null}
                    {selectedGroup !== null ? (
                        <>
                            <GamesList
                                className={styles.games}
                                items={games}
                                participations={participations}
                                canParticipate={canParticipate}
                                withEmailForm={withEmailForm}
                            />
                            {withEmailForm ? (
                                <div className={styles.emailFormContainer}>
                                    <div className={styles.emailFormInner}>
                                        {emailFormTitle !== null ? (
                                            <h2 className={styles.emailTitle}>{emailFormTitle}</h2>
                                        ) : null}
                                        <div className={styles.emailDescription}>
                                            {emailFormDescription || (
                                                <FormattedMessage
                                                    defaultMessage="Enter your email address for additional chances to win!"
                                                    description="Email form description"
                                                />
                                            )}
                                        </div>
                                        <EmailForm
                                            className={styles.emailForm}
                                            placeholder={intl.formatMessage({
                                                defaultMessage: 'email@johndoe.com',
                                                description: 'Email form placeholder',
                                            })}
                                            complete={emailComplete}
                                            onSuccess={onEmailComplete}
                                            inputClassName={styles.emailInput}
                                            inputContainerClassName={styles.emailFormInputContainer}
                                            submitClassName={styles.emailSubmit}
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <GamesGroupsList
                            items={groups}
                            games={games}
                            participations={participations}
                            onClick
                            className={styles.groups}
                        />
                    )}
                </>
            ) : (
                <div className={styles.noGamesContainer}>
                    <div className={styles.noGames}>
                        <h1 className={styles.title}>
                            {ended ? (
                                <FormattedMessage
                                    defaultMessage="No games today{br}{br}The contest is finished."
                                    description="Games page - contest ended message"
                                />
                            ) : themeName === 'nfl' ? (
                                <FormattedMessage
                                    defaultMessage="No matches today{br}{br}Come back soon{br}to play again."
                                    description="Games page - no games today message"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="No games today{br}{br}Come back soon{br}to play again."
                                    description="Games page - no games today message"
                                />
                            )}
                        </h1>
                        <Button href={route('account')} className={styles.button}>
                            <FormattedMessage
                                defaultMessage="Go to your account"
                                description="Button label"
                            />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

PlayGroupsPage.propTypes = propTypes;
PlayGroupsPage.defaultProps = defaultProps;

export default PlayGroupsPage;
